import { ScrollArea, Divider } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavLink } from '@/components/ui/navigation';
import { Stack, Box } from '@/components/ui/layout';
import { Text } from '@/components/ui/core';
import { usePreferencesStore } from '@/stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '@/hooks/useAccessibilityStyles';
import { useVendorContext } from '@/hooks/useVendorContext';
import classes from './AppSidebar.module.css';
import {
  IconDashboard,
  IconBuilding,
  IconPackage,
  IconStar,
  IconCalendarEvent,
  IconShoppingCart,
  IconHistory,
  IconUser,
  IconSettings,
  IconWallet,
  IconFileText,
  IconCashBanknote,
} from '@tabler/icons-react';

interface MenuItemConfig {
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
  disabled?: boolean;
}

interface MenuSectionConfig {
  title: string;
  items: MenuItemConfig[];
}

/**
 * AppSidebar Component
 * Unified sidebar navigation for the vendor portal
 * Shows all vendor navigation in a single menu
 */
export default function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const menuIconOnly = usePreferencesStore((state) => state.menuIconOnly);
  const menuDensity = usePreferencesStore((state) => state.menuDensity);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);
  const { summary } = useVendorContext();

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Get counts from vendor summary for badges
  const productCount = summary?.productCount ?? 0;
  const ratingCount = summary?.ratingCount ?? 0;

  // Menu configuration
  const menuSections: MenuSectionConfig[] = [
    {
      title: 'Main',
      items: [
        {
          label: 'Dashboard',
          icon: <IconDashboard size={20} />,
          path: '/',
        },
        {
          label: 'Business Profile',
          icon: <IconBuilding size={20} />,
          path: '/profile',
        },
      ],
    },
    {
      title: 'Catalog',
      items: [
        {
          label: 'Products',
          icon: <IconPackage size={20} />,
          path: '/products',
          badge: productCount > 0 ? productCount : undefined,
        },
        {
          label: 'Ratings',
          icon: <IconStar size={20} />,
          path: '/ratings',
          badge: ratingCount > 0 ? ratingCount : undefined,
        },
      ],
    },
    {
      title: 'Engagement',
      items: [
        {
          label: 'Events',
          icon: <IconCalendarEvent size={20} />,
          path: '/events',
        },
        {
          label: 'Orders',
          icon: <IconShoppingCart size={20} />,
          path: '/orders',
        },
      ],
    },
    {
      title: 'Finance',
      items: [
        {
          label: 'Earnings',
          icon: <IconWallet size={20} />,
          path: '/finance/earnings',
        },
        {
          label: 'Statement',
          icon: <IconFileText size={20} />,
          path: '/finance/statement',
        },
        {
          label: 'Payouts',
          icon: <IconCashBanknote size={20} />,
          path: '/finance/payouts',
        },
      ],
    },
    {
      title: 'Activity',
      items: [
        {
          label: 'Activity Log',
          icon: <IconHistory size={20} />,
          path: '/logs',
        },
      ],
    },
    {
      title: 'Account',
      items: [
        {
          label: 'Account',
          icon: <IconUser size={20} />,
          path: '/account',
        },
        {
          label: 'Settings',
          icon: <IconSettings size={20} />,
          path: '/settings',
        },
      ],
    },
  ];

  // Check if a path is active
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Get padding based on density
  const getPadding = () => {
    switch (menuDensity) {
      case 'compact':
        return 'xs';
      case 'comfortable':
        return 'md';
      default:
        return 'sm';
    }
  };

  return (
    <ScrollArea
      h="100%"
      scrollbarSize={6}
      type="hover"
      scrollHideDelay={500}
      classNames={{
        viewport: classes.viewport,
        scrollbar: classes.scrollbar,
        thumb: classes.thumb,
      }}
      styles={{
        root: {
          height: '100%',
        },
      }}
    >
      <Stack gap="xs" p={getPadding()}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={section.title}>
            {/* Section divider (not for first section) */}
            {sectionIndex > 0 && <Divider my="xs" />}

            {/* Section title (hidden in icon-only mode) */}
            {!menuIconOnly && (
              <Text
                size="xs"
                fw={600}
                c="dimmed"
                tt="uppercase"
                mb="xs"
                px="sm"
                style={accessibilityStyles}
              >
                {section.title}
              </Text>
            )}

            {/* Section items */}
            <Stack gap={4}>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  label={menuIconOnly ? undefined : item.label}
                  leftSection={item.icon}
                  active={isActive(item.path)}
                  onClick={() => navigate(item.path)}
                  disabled={item.disabled}
                  styles={{
                    root: {
                      borderRadius: 'var(--mantine-radius-md)',
                      justifyContent: menuIconOnly ? 'center' : 'flex-start',
                    },
                    section: {
                      marginRight: menuIconOnly ? 0 : undefined,
                    },
                  }}
                  title={menuIconOnly ? item.label : undefined}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </ScrollArea>
  );
}
